import { getUserTenureOptions, getOverdraftOptions, getAchOptions, getBalanceOptions, sanitizeBalanceRules } from './utils/available-balance-options';

class ConfigurationDelegate {
    // Step management
    setCurrentStep(currentStep) {
        this.setState({ currentStep });
    }

    // Segmentation delegates - update segment_policy object
    setBankingTenure(bankingTenure) {
        this.setState(
            (prevState) => ({
                segment_policy: {
                    ...prevState.segment_policy,
                    bankingTenure
                }
            }),
            () => this.sanitizeBalanceRulesWithSegmentation()
        );
    }

    setAccountTypes(accountTypes) {
        this.setState(
            (prevState) => ({
                segment_policy: {
                    ...prevState.segment_policy,
                    accountTypes
                }
            }),
            () => this.sanitizeBalanceRulesWithSegmentation()
        );
    }

    setAvgDailyBalance(avgDailyBalance) {
        this.setState(
            (prevState) => ({
                segment_policy: {
                    ...prevState.segment_policy,
                    avgDailyBalance
                }
            }),
            () => this.sanitizeBalanceRulesWithSegmentation()
        );
    }

    setOverdraftHistory(overdraftHistory) {
        this.setState(
            (prevState) => ({
                segment_policy: {
                    ...prevState.segment_policy,
                    overdraftHistory
                }
            }),
            () => this.sanitizeBalanceRulesWithSegmentation()
        );
    }

    setReturnedACHs(returnedACHs) {
        this.setState(
            (prevState) => ({
                segment_policy: {
                    ...prevState.segment_policy,
                    returnedACHs
                }
            }),
            () => this.sanitizeBalanceRulesWithSegmentation()
        );
    }

    // Set entire segment_policy object (for loading from service)
    setSegmentPolicy(segmentPolicy) {
        this.setState(
            {
                segment_policy: segmentPolicy
            },
            () => {
                this.updateSegmentationSwitchState(segmentPolicy);
                this.sanitizeBalanceRulesWithSegmentation();
            }
        );
    }

    updateSegmentationSwitchState(segment_policy) {
        if (!segment_policy) return;
        const segmentationEnabled = { ...this.state.segmentation_enabled };
        for (const fieldName in segment_policy) {
            if (!(fieldName in this.state.segmentation_enabled)) continue;
            segmentationEnabled[fieldName] = segment_policy[fieldName] !== null && segment_policy[fieldName] !== '';
        }
        this.setState({ segmentation_enabled: segmentationEnabled });
    }

    // Toggle field enabled/disabled
    toggleFieldEnabled(fieldName) {
        this.setState(
            (prevState) => {
                const currentlyEnabled = prevState.segmentation_enabled[fieldName];
                return {
                    segmentation_enabled: {
                        ...prevState.segmentation_enabled,
                        [fieldName]: !currentlyEnabled
                    },
                    segment_policy: {
                        ...prevState.segment_policy,
                        [fieldName]: currentlyEnabled ? null : prevState.segment_policy[fieldName]
                    }
                };
            },
            () => this.sanitizeBalanceRulesWithSegmentation()
        );
    }

    // Set entire segmentation_enabled object (for loading from service)
    setSegmentationEnabled(segmentationEnabled) {
        this.setState(
            {
                segmentation_enabled: segmentationEnabled || {
                    bankingTenure: true,
                    accountTypes: true,
                    avgDailyBalance: true,
                    overdraftHistory: true,
                    returnedACHs: true
                }
            },
            () => this.sanitizeBalanceRulesWithSegmentation()
        );
    }

    // Segmentation mode management
    setSegmentationMode(mode) {
        this.setState((prevState) => ({
            segment_policy: {
                ...prevState.segment_policy,
                mode
            }
        }));
    }

    // Set block out of segmentation flag
    setBlockOutOfSegmentation(blockOutOfSegmentation) {
        this.setState((prevState) => ({
            segment_policy: {
                ...prevState.segment_policy,
                blockOutOfSegmentation: blockOutOfSegmentation === true
            }
        }));
    }

    // Whitelist management
    setWhitelistInput(whitelistInput) {
        this.setState({ whitelistInput });
    }

    addToWhitelist(memberId) {
        this.setState((prevState) => ({
            segment_policy: {
                ...prevState.segment_policy,
                whitelist: [...prevState.segment_policy.whitelist, memberId]
            }
        }));
    }

    removeFromWhitelist(memberId) {
        this.setState((prevState) => ({
            segment_policy: {
                ...prevState.segment_policy,
                whitelist: prevState.segment_policy.whitelist.filter((id) => id !== memberId)
            }
        }));
    }

    // Available Balance delegates
    // threshold: percentage value from UI (e.g., 70), converts to decimal for storage (e.g., 0.7)
    setThreshold(threshold) {
        // Allow null/undefined to clear the field completely
        if (threshold === null || threshold === undefined || threshold === '') {
            this.setState({ threshold: null });
            return;
        }

        // Convert percentage to decimal (70 -> 0.7, 75 -> 0.75)
        // Round to 4 decimal places to avoid floating point precision issues
        const decimalValue = Math.round((threshold / 100) * 10000) / 10000;
        this.setState({ threshold: decimalValue });
    }

    // doubleDipCap: percentage value from UI (e.g., 50), store as decimal (0.5)
    setDoubleDipCap(cap) {
        if (cap === null || cap === undefined || cap === '') {
            this.setState({ doubleDipCap: null });
            return;
        }

        const decimalValue = Math.round((cap / 100) * 10000) / 10000;
        this.setState({ doubleDipCap: decimalValue });
    }

    // Set entire balance_rules object (for loading from service)
    setBalanceRules(balanceRules, balanceEnabledState, segmentPolicyOverride, segmentationEnabledOverride) {
        const defaultRules = {
            userTenureRules: [{ id: 1, condition: '60-89-days', deduction: 10 }],
            overdraftRules: [],
            achRules: [],
            employmentRules: [],
            balanceRules: []
        };

        const normalizedRules = {
            userTenureRules: (balanceRules?.userTenureRules || defaultRules.userTenureRules).map((rule) => ({
                id: rule.id,
                condition: rule.condition,
                deduction: Number(rule.deduction ?? 0)
            })),
            overdraftRules: (balanceRules?.overdraftRules || defaultRules.overdraftRules).map((rule) => ({
                id: rule.id,
                condition: rule.condition,
                deduction: Number(rule.deduction ?? 0)
            })),
            achRules: (balanceRules?.achRules || defaultRules.achRules).map((rule) => ({
                id: rule.id,
                condition: rule.condition,
                deduction: Number(rule.deduction ?? 0)
            })),
            employmentRules: (balanceRules?.employmentRules || defaultRules.employmentRules).map((rule) => ({
                id: rule.id,
                condition: rule.condition,
                deduction: Number(rule.deduction ?? 0)
            })),
            balanceRules: (balanceRules?.balanceRules || defaultRules.balanceRules).map((rule) => ({
                id: rule.id,
                condition: rule.condition,
                deduction: Number(rule.deduction ?? 0)
            }))
        };

        const inferEnabled = (rulesKey, fallback = false) => {
            if (balanceEnabledState && typeof balanceEnabledState[rulesKey] === 'boolean') {
                return balanceEnabledState[rulesKey];
            }

            const originalRules = balanceRules?.[`${rulesKey}Rules`];
            if (originalRules && originalRules.length > 0) {
                return !originalRules.every((rule) => rule.disabled === true);
            }

            return fallback;
        };

        const balanceEnabled = {
            userTenure: inferEnabled('userTenure', true),
            overdraft: inferEnabled('overdraft', false),
            ach: inferEnabled('ach', false),
            employment: inferEnabled('employment', false),
            balance: inferEnabled('balance', false)
        };

        const segmentPolicy = segmentPolicyOverride ?? this.state.segment_policy;
        const segmentationEnabled = segmentationEnabledOverride ?? this.state.segmentation_enabled;
        const effectiveSegmentPolicy = {
            ...segmentPolicy,
            mode: segmentPolicy?.mode || this.state.segment_policy?.mode
        };

        const sanitizedRules = sanitizeBalanceRules(normalizedRules, effectiveSegmentPolicy, segmentationEnabled);

        this.setState({
            balance_rules: sanitizedRules,
            balance_enabled: balanceEnabled
        });
    }

    setBalanceEnabled(balanceEnabled) {
        this.setState({
            balance_enabled: {
                userTenure: balanceEnabled?.userTenure ?? true,
                overdraft: balanceEnabled?.overdraft ?? false,
                ach: balanceEnabled?.ach ?? false,
                employment: balanceEnabled?.employment ?? false,
                balance: balanceEnabled?.balance ?? false
            }
        });
    }

    toggleBalanceRuleGroup(groupKey) {
        this.setState((prevState) => ({
            balance_enabled: {
                ...prevState.balance_enabled,
                [groupKey]: !prevState.balance_enabled[groupKey]
            }
        }));
    }

    // Fee Structure delegates
    setFee(fee) {
        // Ensure fee is always a number
        const numericFee = typeof fee === 'string' ? parseFloat(fee) : fee || 0;
        this.setState({ fee: isNaN(numericFee) ? 0 : numericFee });
    }

    setFeeEnabled(feeEnabled) {
        this.setState({
            fee_enabled: {
                instantFee: feeEnabled?.instantFee ?? true,
                volumeTiers: feeEnabled?.volumeTiers ?? true,
                freeAdvances: feeEnabled?.freeAdvances ?? true
            }
        });
    }

    toggleFeeSection(sectionKey) {
        this.setState((prevState) => ({
            fee_enabled: {
                ...prevState.fee_enabled,
                [sectionKey]: !prevState.fee_enabled?.[sectionKey]
            }
        }));
    }

    setStandardFreeAdvanceTime(standardFreeAdvanceTime) {
        this.setState((prevState) => ({
            fee_policy: {
                ...prevState.fee_policy,
                standardFreeAdvanceTime: standardFreeAdvanceTime || 1
            }
        }));
    }

    setStandardFreeAdvanceUnit(standardFreeAdvanceUnit) {
        this.setState((prevState) => ({
            fee_policy: {
                ...prevState.fee_policy,
                standardFreeAdvanceUnit
            }
        }));
    }

    setFeeInstance(feeInstance) {
        this.setState((prevState) => ({
            fee_policy: {
                ...prevState.fee_policy,
                feeInstance
            }
        }));
    }
    
    setFeeStrategy(feeStrategy) {
        this.setState((prevState) => ({
            fee_policy: {
                ...prevState.fee_policy,
                feeStrategy
            }
        }));
    }

    // Set entire fee_policy object (for loading from service)
    setFeePolicy(feePolicy) {
        this.setState({
            fee_policy: feePolicy || {
                standardFreeAdvanceTime: 1,
                standardFreeAdvanceUnit: 'business-days',
                feeInstance: 'on-advance',
                feeStrategy: 'on-top-of-advance'
            }
        });
    }

    // Set entire fee_rules object (for loading from service)
    setFeeRules(feeRules) {
        this.setState({
            fee_rules: feeRules || {
                volumeTierRules: [{ id: 1, condition: 'less-than-50', fee: 1.0 }],
                freeAdvanceRules: [{ id: 1, count: 1, condition: 'first-advance' }]
            }
        });
    }

    // Limits delegates - update limit_policy object
    setMaxAdvances(maxAdvances) {
        this.setState((prevState) => ({
            limit_policy: {
                ...prevState.limit_policy,
                maxAdvances: maxAdvances === null || maxAdvances === undefined ? null : maxAdvances
            }
        }));
    }

    setAdvanceTimeframe(advanceTimeframe) {
        this.setState((prevState) => ({
            limit_policy: {
                ...prevState.limit_policy,
                advanceTimeframe
            }
        }));
    }

    setMinAdvanceAmount(minAdvanceAmount) {
        this.setState((prevState) => ({
            limit_policy: {
                ...prevState.limit_policy,
                minAdvanceAmount: minAdvanceAmount || 5
            }
        }));
    }

    setMaxAdvanceAmount(maxAdvanceAmount) {
        this.setState((prevState) => ({
            limit_policy: {
                ...prevState.limit_policy,
                maxAdvanceAmount: maxAdvanceAmount || 700
            }
        }));
    }

    setMaxAdvanceVolume(maxAdvanceVolume) {
        this.setState((prevState) => ({
            limit_policy: {
                ...prevState.limit_policy,
                maxAdvanceVolume: maxAdvanceVolume || 1500
            }
        }));
    }

    // Set entire limit_policy object (for loading from service)
    setLimitPolicy(limitPolicy) {
        this.setState({
            limit_policy: limitPolicy || {
                maxAdvances: 12,
                advanceTimeframe: 'pay-period',
                minAdvanceAmount: 5,
                maxAdvanceAmount: 700,
                maxAdvanceVolume: 1500
            }
        });
    }

    setLimitsEnabled(limitsEnabled) {
        this.setState({
            limits_enabled: {
                maxAdvances: limitsEnabled?.maxAdvances ?? true,
                advanceAmounts: limitsEnabled?.advanceAmounts ?? true,
                totalVolume: limitsEnabled?.totalVolume ?? true
            }
        });
    }

    toggleLimitsSection(sectionKey) {
        this.setState((prevState) => ({
            limits_enabled: {
                ...prevState.limits_enabled,
                [sectionKey]: !prevState.limits_enabled?.[sectionKey]
            }
        }));
    }

    // Rule management for Available Balance - update balance_rules
    addRule(ruleType, defaultCondition) {
        const stateKey = `${ruleType}Rules`;
        const currentRules = this.state.balance_rules[stateKey] || [];
        const newId = currentRules.length > 0 ? Math.max(...currentRules.map((r) => r.id)) + 1 : 1;

        const resolveDefaultCondition = () => {
            if (defaultCondition) return defaultCondition;
            switch (ruleType) {
                case 'userTenure': {
                    const options = getUserTenureOptions(this.state.segment_policy, this.state.segmentation_enabled);
                    return options[0]?.value || null;
                }
                case 'overdraft': {
                    const options = getOverdraftOptions(this.state.segment_policy, this.state.segmentation_enabled);
                    return options[0]?.value || null;
                }
                case 'ach': {
                    const options = getAchOptions(this.state.segment_policy, this.state.segmentation_enabled);
                    return options[0]?.value || null;
                }
                case 'balance': {
                    const options = getBalanceOptions(this.state.segment_policy, this.state.segmentation_enabled);
                    return options[0]?.value || null;
                }
                default:
                    return null;
            }
        };

        const condition = resolveDefaultCondition();
        if (!condition) {
            return;
        }

        const newRule = {
            id: newId,
            condition,
            deduction: 10
        };

        this.setState((prevState) => ({
            balance_rules: {
                ...prevState.balance_rules,
                [stateKey]: [...currentRules, newRule]
            }
        }));
    }

    removeRule(ruleType, ruleId) {
        const stateKey = `${ruleType}Rules`;
        this.setState((prevState) => ({
            balance_rules: {
                ...prevState.balance_rules,
                [stateKey]: prevState.balance_rules[stateKey].filter((rule) => rule.id !== ruleId)
            }
        }));
    }

    updateRule(ruleType, ruleId, field, value) {
        const stateKey = `${ruleType}Rules`;
        this.setState((prevState) => ({
            balance_rules: {
                ...prevState.balance_rules,
                [stateKey]: prevState.balance_rules[stateKey].map((rule) => (rule.id === ruleId ? { ...rule, [field]: value } : rule))
            }
        }));
    }

    sanitizeBalanceRulesWithSegmentation() {
        this.setState((prevState) => {
            const sanitized = sanitizeBalanceRules(prevState.balance_rules, prevState.segment_policy, prevState.segmentation_enabled);
            if (JSON.stringify(sanitized) === JSON.stringify(prevState.balance_rules)) {
                return null;
            }
            return { balance_rules: sanitized };
        });
    }

    // Rule management for Fee Structure - update fee_rules
    addVolumeTierRule() {
        const { volumeTierRules } = this.state.fee_rules;
        const newId = volumeTierRules.length > 0 ? Math.max(...volumeTierRules.map((r) => r.id)) + 1 : 1;
        const newRule = {
            id: newId,
            condition: 'less-than-50',
            fee: 1.0
        };
        this.setState((prevState) => ({
            fee_rules: {
                ...prevState.fee_rules,
                volumeTierRules: [...volumeTierRules, newRule]
            }
        }));
    }

    removeVolumeTierRule(ruleId) {
        this.setState((prevState) => ({
            fee_rules: {
                ...prevState.fee_rules,
                volumeTierRules: prevState.fee_rules.volumeTierRules.filter((rule) => rule.id !== ruleId)
            }
        }));
    }

    updateVolumeTierRule(ruleId, field, value) {
        this.setState((prevState) => ({
            fee_rules: {
                ...prevState.fee_rules,
                volumeTierRules: prevState.fee_rules.volumeTierRules.map((rule) => (rule.id === ruleId ? { ...rule, [field]: value } : rule))
            }
        }));
    }

    addFreeAdvanceRule() {
        const { freeAdvanceRules } = this.state.fee_rules;
        const newId = freeAdvanceRules.length > 0 ? Math.max(...freeAdvanceRules.map((r) => r.id)) + 1 : 1;
        const newRule = {
            id: newId,
            count: 1,
            condition: 'first-advance'
        };
        this.setState((prevState) => ({
            fee_rules: {
                ...prevState.fee_rules,
                freeAdvanceRules: [...freeAdvanceRules, newRule]
            }
        }));
    }

    removeFreeAdvanceRule(ruleId) {
        this.setState((prevState) => ({
            fee_rules: {
                ...prevState.fee_rules,
                freeAdvanceRules: prevState.fee_rules.freeAdvanceRules.filter((rule) => rule.id !== ruleId)
            }
        }));
    }

    updateFreeAdvanceRule(ruleId, field, value) {
        this.setState((prevState) => ({
            fee_rules: {
                ...prevState.fee_rules,
                freeAdvanceRules: prevState.fee_rules.freeAdvanceRules.map((rule) => (rule.id === ruleId ? { ...rule, [field]: value } : rule))
            }
        }));
    }

    // Common delegates
    setLoading(loading) {
        this.setState({ loading });
    }

    setError(error) {
        this.setState({ error });
    }

    // Transformation helper methods - Convert between camelCase (frontend) and snake_case (API)

    // Transform segment_policy from camelCase to snake_case
    // Only includes fields that are actually in the Segmentation screen
    transformSegmentPolicyToSnakeCase(segmentPolicy) {
        const transformed = {};

        // Always include mode and whitelist
        transformed.mode = segmentPolicy.mode || 'restricted';
        transformed.whitelist = segmentPolicy.whitelist || [];

        // Always include block out of segmentation flag (snake_case)
        transformed.block_out_of_segmentation = segmentPolicy.blockOutOfSegmentation === true;

        // Only include the 5 fields that are actually in the Segmentation form
        if (segmentPolicy.bankingTenure !== undefined && segmentPolicy.bankingTenure !== '') {
            transformed.banking_tenure = segmentPolicy.bankingTenure;
        }
        if (segmentPolicy.accountTypes !== undefined && segmentPolicy.accountTypes !== '') {
            transformed.account_types = segmentPolicy.accountTypes;
        }
        if (segmentPolicy.avgDailyBalance !== undefined && segmentPolicy.avgDailyBalance !== '') {
            transformed.avg_daily_balance = segmentPolicy.avgDailyBalance;
        }
        if (segmentPolicy.overdraftHistory !== undefined && segmentPolicy.overdraftHistory !== '') {
            transformed.overdraft_nsf_history = segmentPolicy.overdraftHistory;
        }
        if (segmentPolicy.returnedACHs !== undefined && segmentPolicy.returnedACHs !== '') {
            transformed.account_recovery_disputes = segmentPolicy.returnedACHs;
        }

        return transformed;
    }

    // Transform segment_policy from snake_case (API) to camelCase (frontend)
    transformSegmentPolicyToCamelCase(segmentPolicy) {
        return {
            mode: segmentPolicy.mode || 'restricted',
            whitelist: segmentPolicy.whitelist || [],
            bankingTenure: segmentPolicy.banking_tenure || '',
            accountTypes: segmentPolicy.account_types || '',
            avgDailyBalance: segmentPolicy.avg_daily_balance || '',
            overdraftHistory: segmentPolicy.overdraft_nsf_history || '',
            returnedACHs: segmentPolicy.account_recovery_disputes || '',
            blockOutOfSegmentation: segmentPolicy.block_out_of_segmentation === true
        };
    }

    // Transform balance_rules from camelCase to snake_case
    transformBalanceRulesToSnakeCase(balanceRules, balanceEnabled = this.state.balance_enabled) {
        const withDisabledFlag = (rules = [], enabled = true) =>
            (rules || []).map((rule) => ({
                ...rule,
                disabled: !enabled
            }));

        return {
            user_tenure_rules: withDisabledFlag(balanceRules.userTenureRules, balanceEnabled?.userTenure ?? true),
            overdraft_rules: withDisabledFlag(balanceRules.overdraftRules, balanceEnabled?.overdraft ?? false),
            ach_rules: withDisabledFlag(balanceRules.achRules, balanceEnabled?.ach ?? false),
            employment_rules: withDisabledFlag(balanceRules.employmentRules, balanceEnabled?.employment ?? false),
            balance_rules: withDisabledFlag(balanceRules.balanceRules, balanceEnabled?.balance ?? false)
        };
    }

    // Transform balance_rules from snake_case (API) to camelCase (frontend)
    transformBalanceRulesToCamelCase(balanceRules) {
        const normalizeRules = (rules = []) =>
            rules.map((rule) => ({
                id: rule.id,
                condition: rule.condition,
                deduction: Number(rule.deduction ?? 0),
                disabled: rule.disabled ?? false
            }));

        const userTenureRules = normalizeRules(balanceRules.user_tenure_rules || []);
        const overdraftRules = normalizeRules(balanceRules.overdraft_rules || []);
        const achRules = normalizeRules(balanceRules.ach_rules || []);
        const employmentRules = normalizeRules(balanceRules.employment_rules || []);
        const balanceRuleList = normalizeRules(balanceRules.balance_rules || []);

        const balanceEnabled = {
            userTenure: userTenureRules.length === 0 ? false : !userTenureRules.every((rule) => rule.disabled),
            overdraft: overdraftRules.length === 0 ? false : !overdraftRules.every((rule) => rule.disabled),
            ach: achRules.length === 0 ? false : !achRules.every((rule) => rule.disabled),
            employment: employmentRules.length === 0 ? false : !employmentRules.every((rule) => rule.disabled),
            balance: balanceRuleList.length === 0 ? false : !balanceRuleList.every((rule) => rule.disabled)
        };

        return {
            userTenureRules: userTenureRules.map(({ disabled, ...rest }) => rest),
            overdraftRules: overdraftRules.map(({ disabled, ...rest }) => rest),
            achRules: achRules.map(({ disabled, ...rest }) => rest),
            employmentRules: employmentRules.map(({ disabled, ...rest }) => rest),
            balanceRules: balanceRuleList.map(({ disabled, ...rest }) => rest),
            balanceEnabled
        };
    }

    // Transform fee_policy from camelCase to snake_case
    transformFeePolicyToSnakeCase(feePolicy) {
        return {
            standard_free_advance_time: feePolicy.standardFreeAdvanceTime || null,
            standard_free_advance_unit: feePolicy.standardFreeAdvanceUnit || null,
            fee_instance: feePolicy.feeInstance || null,
            fee_strategy: feePolicy.feeStrategy || null
        };
    }

    // Transform fee_policy from snake_case (API) to camelCase (frontend)
    transformFeePolicyToCamelCase(feePolicy) {
        return {
            standardFreeAdvanceTime: feePolicy.standard_free_advance_time || 1,
            standardFreeAdvanceUnit: feePolicy.standard_free_advance_unit || 'business-days',
            feeInstance: feePolicy.fee_instance || 'on-advance',
            feeStrategy: feePolicy.fee_strategy || 'on-top-of-advance'
        };
    }

    // Transform fee_rules from camelCase to snake_case
    transformFeeRulesToSnakeCase(feeRules) {
        return {
            volume_tier_rules: feeRules.volumeTierRules || [],
            free_advance_rules: feeRules.freeAdvanceRules || []
        };
    }

    // Transform fee_rules from snake_case (API) to camelCase (frontend)
    transformFeeRulesToCamelCase(feeRules) {
        return {
            volumeTierRules: feeRules.volume_tier_rules || [{ id: 1, condition: 'less-than-50', fee: 1.0 }],
            freeAdvanceRules: feeRules.free_advance_rules || [{ id: 1, count: 1, condition: 'first-advance' }]
        };
    }

    // Transform limit_policy from camelCase to snake_case
    transformLimitPolicyToSnakeCase(limitPolicy) {
        return {
            max_advances: limitPolicy.maxAdvances || null,
            advance_timeframe: limitPolicy.advanceTimeframe || null,
            min_advance_amount: limitPolicy.minAdvanceAmount || null,
            max_advance_amount: limitPolicy.maxAdvanceAmount || null,
            max_advance_volume: limitPolicy.maxAdvanceVolume || null
        };
    }

    // Transform limit_policy from snake_case (API) to camelCase (frontend)
    transformLimitPolicyToCamelCase(limitPolicy) {
        return {
            maxAdvances: limitPolicy.max_advances || 12,
            advanceTimeframe: limitPolicy.advance_timeframe || 'pay-period',
            minAdvanceAmount: limitPolicy.min_advance_amount || 5,
            maxAdvanceAmount: limitPolicy.max_advance_amount || 700,
            maxAdvanceVolume: limitPolicy.max_advance_volume || 1500
        };
    }
}

export default ConfigurationDelegate;
